// frontend/book-app-frontend/src/components/TaskManagement_updated.js
import { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import ChapterTextEditor from '../ChapterTextEditor/ChapterTextEditor';
import { AuthContext } from '../../contexts/AuthContext';
import { LanguageContext } from '../../contexts/LanguageProvider';
import { Card, ListGroup, Button, Badge, Modal, Form, Spinner, Tabs, Tab } from 'react-bootstrap';
import PaginationComponent from '../PaginationComponent';
import BrandSelect from '../common/BrandSelect';
import { useAlertContext } from '../../contexts/AlertContext';
import { API_ENDPOINTS } from '../../configs/apiConfig';
import OwnedTaskDetails from './OwnedTaskDetails';

import translations from './translations';
import TaskDetails from './TaskDetails';
import { FaTasks } from 'react-icons/fa';
function TaskManagement() {
  // Estados originais
  const [allAssignedTasks, setAllAssignedTasks] = useState([]);
  const [allOwnedChapterTasks, setAllOwnedChapterTasks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [ownedChapterTasks, setOwnedChapterTasks] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const { isLoggedIn, username } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const { showSuccess, showError } = useAlertContext();

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;

  // Estado para aba de status
  const [activeStatusTab, setActiveStatusTab] = useState('OPEN');

  // Estado para aba principal (assigned ou owned)
  const [activeTab, setActiveTab] = useState('assigned');

  // Estados para o modal de criação de tarefas
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [availableChapters, setAvailableChapters] = useState([]);
  const [selectedChapterIdForNewTask, setSelectedChapterIdForNewTask] = useState('');
  const [commentsForSelectedChapter, setCommentsForSelectedChapter] = useState([]);
  const [selectedCommentsForNewTask, setSelectedCommentsForNewTask] = useState([]);
  const [availableEditors, setAvailableEditors] = useState([]);
  const [selectedEditorForNewTask, setSelectedEditorForNewTask] = useState('');
  const [newTaskInstructions, setNewTaskInstructions] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewTask, setPreviewTask] = useState(null);
  const [oldContent, setOldContent] = useState(null);

  // Modal de troca de responsável
  const [showChangeResponsibleModal, setShowChangeResponsibleModal] = useState(false);
  const [selectedTaskForChange, setSelectedTaskForChange] = useState(null);

  const t = translations[language] || translations.pt;

  // Função para download de arquivo Base64 (movida para o escopo superior para reutilização)
  const handleDownload = useCallback((base64String, fileName) => {
    if (!base64String || !fileName) return;

    // Decodificar a string Base64
    const link = document.createElement("a");

    // Inferir o tipo MIME do nome do arquivo
    let mimeType = "application/octet-stream"; // Tipo padrão para binário
    const parts = fileName.split(".");
    const extension = parts.length > 1 ? parts.pop().toLowerCase() : "";

    switch (extension) {
      case "png": mimeType = "image/png"; break;
      case "jpg":
      case "jpeg": mimeType = "image/jpeg"; break;
      case "gif": mimeType = "image/gif"; break;
      case "bmp": mimeType = "image/bmp"; break;
      case "webp": mimeType = "image/webp"; break;

      case "pdf": mimeType = "application/pdf"; break;
      case "txt": mimeType = "text/plain"; break;
      case "csv": mimeType = "text/csv"; break;
      case "json": mimeType = "application/json"; break;
      case "xml": mimeType = "application/xml"; break;
      case "md": mimeType = "text/markdown"; break;

      case "zip": mimeType = "application/zip"; break;
      case "rar": mimeType = "application/vnd.rar"; break;
      case "7z": mimeType = "application/x-7z-compressed"; break;

      default: mimeType = "application/octet-stream"; break;
    }

    link.href = `data:${mimeType};base64,${base64String}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const orderedTasks = useCallback((tasks) => {
    const open = [];
    const waiting = [];
    const closed = [];

    tasks.forEach(task => {
      switch (task.state) {
        case 'OPEN':
          open.push(task);
          break;
        case 'WAITING':
          waiting.push(task);
          break;
        case 'CLOSED':
          closed.push(task);
          break;
        default:
          break;
      }
    });

    return [...open, ...waiting, ...closed];
  }, []);

  // Fetch assigned tasks
  const fetchAllTasks = useCallback(async () => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const auth = localStorage.getItem('auth');
      const myTaskResponse = await axios.get(API_ENDPOINTS.TASKS_ASSIGNED, {
        headers: { 'Authorization': `Basic ${auth}` }
      });

      setAllAssignedTasks(orderedTasks(myTaskResponse.data));

      const ownedChapterResponse = await axios.get(API_ENDPOINTS.TASKS_BY_OWNED_CHAPTERS, {
        headers: { 'Authorization': `Basic ${auth}` }
      });

      setAllOwnedChapterTasks(orderedTasks(ownedChapterResponse.data));
    } catch (error) {
      showError(t.failedToFetch + error.message);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, orderedTasks, t.failedToFetch, showError]);

  useEffect(() => {
    fetchAllTasks();
  }, [isLoggedIn, fetchAllTasks]);

  // Efeito para atualizar as tarefas exibidas na página atual
  useEffect(() => {
    const currentTasks = activeTab === 'assigned' ? allAssignedTasks : allOwnedChapterTasks;
    const filteredTasks = currentTasks.filter(task => task.state === activeStatusTab);
    const startIndex = (currentPage - 1) * tasksPerPage;
    const endIndex = startIndex + tasksPerPage;

    if (activeTab === 'assigned') {
      setTasks(filteredTasks.slice(startIndex, endIndex));
    } else {
      setOwnedChapterTasks(filteredTasks.slice(startIndex, endIndex));
    }

    setTotalTasks(filteredTasks.length);
    setTotalPages(Math.ceil(filteredTasks.length / tasksPerPage));
  }, [allAssignedTasks, allOwnedChapterTasks, currentPage, tasksPerPage, activeTab, activeStatusTab]);


  // Fetch data needed for creating tasks and for the new responsible dropdown
  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchCreationData = async () => {
      try {
        setLoading(true);
        const auth = localStorage.getItem('auth');

        const chaptersResponse = await axios.get(API_ENDPOINTS.CHAPTERS_OWNED(1), {
          headers: { 'Authorization': `Basic ${auth}` }
        });
        setAvailableChapters(chaptersResponse.data);

        const editorsResponse = await axios.get(API_ENDPOINTS.USERS_EDITORS, {
          headers: { 'Authorization': `Basic ${auth}` }
        });
        setAvailableEditors(editorsResponse.data);
      } catch (error) {
        // Se o modal não estiver aberto, apenas loga o erro, mas não mostra o alert para não ser intrusivo
        if (showCreateTaskModal) {
          showError(`${t.failedToLoadChapters} / ${t.failedToLoadEditors}: ${error.message}`);
        } else {
          console.error(`${t.failedToLoadChapters} / ${t.failedToLoadEditors}: ${error.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCreationData();
  }, [isLoggedIn, t.failedToLoadChapters, t.failedToLoadEditors, showError, showCreateTaskModal]);

  // Fetch comments when a chapter ID is selected
  useEffect(() => {
    if (!isLoggedIn || !selectedChapterIdForNewTask) {
      setCommentsForSelectedChapter([]);
      return;
    }

    const fetchComments = async () => {
      try {
        setLoading(true);
        const auth = localStorage.getItem('auth');
        const commentsResponse = await axios.get(
          API_ENDPOINTS.COMMENTS_BY_CHAPTER(selectedChapterIdForNewTask),
          { headers: { 'Authorization': `Basic ${auth}` } }
        );

        setCommentsForSelectedChapter(commentsResponse.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } catch (error) {
        showError(`${t.failedToLoadComments}: ${error.message}`);
        setCommentsForSelectedChapter([]);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [isLoggedIn, selectedChapterIdForNewTask, t.failedToLoadComments, showError]);

  // Função para mudar de página

  // Função para atualizar o responsável pela tarefa
  const handleUpdateTaskResponsible = async (taskId, newResponsibleId, newResponsibleName) => {
    // 1. Converte para número e valida
    const responsibleId = parseInt(newResponsibleId);
    if (isNaN(responsibleId)) {
      showError(t.selectValidResponsible || 'Por favor, selecione um responsável válido.');
      return;
    }

    // 2. Log para debug (já está no seu código, mas vou manter)
    console.log('Updating task', taskId, 'to new responsible', responsibleId);

    try {
      const auth = localStorage.getItem('auth');

      const response = await axios.put(
        // Assumindo que você definiu este endpoint em outro lugar
        API_ENDPOINTS.TASKS_UPDATE_RESPONSIBLE(taskId),
        // 3. Corpo da requisição ajustado para o que você usa
        { id: responsibleId },
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // 4. Lógica de atualização de estado (do meu código original)
      const updatedTask = response.data;
      setAllAssignedTasks(prev => prev.map(task => task.id === taskId ? updatedTask : task));
      setAllOwnedChapterTasks(prev => prev.map(task => task.id === taskId ? updatedTask : task));

      await fetchAllTasks();
      // atualizar o responsável pela tarefa
      setSelectedTaskForChange(task => ({ ...task, responsible: { id: responsibleId, name: '*' + newResponsibleName + '* ' + t.updated } }));
      showSuccess(t.responsibleUpdatedSuccessfully || 'Responsável pela tarefa atualizado com sucesso!');
    } catch (error) {
      showError(t.failedToUpdateResponsible || 'Falha ao atualizar o responsável pela tarefa: ' + error.message);
    }
  };
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setEditingTask(null);
    setPreviewTask(null);
  };

  // Função para mudar de aba principal
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setActiveStatusTab('OPEN');
    setEditingTask(null);
    setPreviewTask(null);
  };

  // Função para mudar de aba de status
  const handleStatusTabChange = (status) => {
    setActiveStatusTab(status);
    setCurrentPage(1);
    setEditingTask(null);
    setPreviewTask(null);
  };

  const handleFetchChapterContent = async (task, type) => {
    try {
      setLoading(true);
      const auth = localStorage.getItem("auth");
      const response = await axios.get(
        API_ENDPOINTS.TASKS_BY_ID_CONTENT(task.id),
        { headers: { Authorization: `Basic ${auth}` } }
      );

      task.content = JSON.stringify(response.data);



      if (type === 'edit') {
        setOldContent(task);
        setEditingTask(task);
      } else if (type === 'preview') {
        const oldResponse = await axios.get(
          API_ENDPOINTS.CHAPTERS_BY_ID(task.chapter.id),
          { headers: { Authorization: `Basic ${auth}` } }
        );
        setOldContent(oldResponse.data);
        setPreviewTask(task);
      }
    } catch (error) {
      showError(t.suggestionFail + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewBack = () => {
    setPreviewTask(null);
  };

  const handleUpdateTaskState = async (taskId, newState) => {
    try {
      const auth = localStorage.getItem('auth');
      const response = await axios.put(
        API_ENDPOINTS.TASKS_BY_ID_STATE(taskId),
        newState,
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setAllAssignedTasks(allAssignedTasks.map(task => task.id === taskId ? response.data : task));
      setAllOwnedChapterTasks(allOwnedChapterTasks.map(task => task.id === taskId ? response.data : task));

      showSuccess(`${t.taskMarkedAs}${newState}`);
    } catch (error) {
      showError(t.failedToUpdate + error.message);
    }
  };

  const handleTaskContentBack = (updatedTask) => {
    setAllAssignedTasks(allAssignedTasks.map(task => task.id === updatedTask.id ? updatedTask : task));
    setAllOwnedChapterTasks(allOwnedChapterTasks.map(task => task.id === updatedTask.id ? updatedTask : task));
    setEditingTask(null);
  };

  // Create Task Modal Handlers
  const handleShowCreateTaskModal = () => setShowCreateTaskModal(true);
  const handleCloseCreateTaskModal = () => {
    setShowCreateTaskModal(false);
    setSelectedChapterIdForNewTask('');
    setCommentsForSelectedChapter([]);
    setSelectedCommentsForNewTask([]);
    setSelectedEditorForNewTask('');
    setNewTaskInstructions('');
  };

  const handleChapterSelectForNewTask = (chapterId) => {
    setSelectedChapterIdForNewTask(chapterId);
    setSelectedCommentsForNewTask([]);
  };

  const toggleCommentSelectionForNewTask = (commentId) => {
    setSelectedCommentsForNewTask(prev =>
      prev.includes(commentId) ? prev.filter(id => id !== commentId) : [...prev, commentId]
    );
  };

  const handleCreateNewTask = async () => {
    if (!selectedChapterIdForNewTask) {
      showError(t.selectChapter);
      return;
    }
    if (!selectedEditorForNewTask) {
      showError(t.selectResponsibleEditor);
      return;
    }

    try {
      const auth = localStorage.getItem('auth');
      const newTask = {
        chapter: { id: selectedChapterIdForNewTask },
        responsible: { id: selectedEditorForNewTask },
        creator: { username: username },
        instructions: newTaskInstructions,
        comments: selectedCommentsForNewTask.map(id => ({ id }))
      };

      await axios.post(
        API_ENDPOINTS.TASKS,
        newTask,
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      await fetchAllTasks();
      showSuccess(t.taskCreatedSuccessfully);
      handleCloseCreateTaskModal();
    } catch (error) {
      showError(`${t.failedToCreateTask}`);
    }
  };

  const handleUpdateTaskStateWithListUpdate = useCallback(async (taskId, newState) => {
    try {
      const auth = localStorage.getItem('auth');
      const response = await axios.put(
        API_ENDPOINTS.TASKS_BY_ID_STATE(taskId),
        newState,
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setAllAssignedTasks(allAssignedTasks.map(task => task.id === taskId ? response.data : task));
      setAllOwnedChapterTasks(allOwnedChapterTasks.map(task => task.id === taskId ? response.data : task));

      showSuccess(`${t.taskMarkedAs}${newState}`);

      if (activeTab === 'owned') {
        const event = new Event('refresh-owned-tasks');
        window.dispatchEvent(event);
      }
    } catch (error) {
      showError(t.failedToUpdate + error.message);
    }
  }, [allAssignedTasks, allOwnedChapterTasks, activeTab, t.taskMarkedAs, t.failedToUpdate, showSuccess, showError]);

  return (
    <div className="page-section">
      <Card>
        <Card.Header as="h2">
          <div className="d-flex justify-content-between align-items-center w-100 mb-3">
            <div className="d-inline-flex align-items-center gap-2"><FaTasks className="text-primary" /> {t.assignedTasks}</div>
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleShowCreateTaskModal}
              >
                {t.createNewTask}
              </Button>
              {(editingTask || previewTask) && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setEditingTask(null);
                    setPreviewTask(null);
                  }}
                >
                  {t.back}
                </Button>
              )}
            </div>
          </div>

          <div className="d-flex gap-2 mb-3">
            <Button
              variant={activeTab === 'assigned' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => handleTabChange('assigned')}
            >
              {t.assignedToMe} {!loading && `(${allAssignedTasks.length})`}
            </Button>
            <Button
              variant={activeTab === 'owned' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => handleTabChange('owned')}
            >
              {t.myChapterTasks} {!loading && `(${allOwnedChapterTasks.length})`}
            </Button>
          </div>

          <Tabs
            activeKey={activeStatusTab}
            onSelect={(key) => handleStatusTabChange(key)}
            style={{ fontSize: '0.9rem' }}
          >
            <Tab eventKey="OPEN" title={`${t.currentOpenTask}`} />
            <Tab eventKey="WAITING" title={`${t.waitingForReview}`} />
            <Tab eventKey="CLOSED" title={`${t.closedTasks}`} />
          </Tabs>
        </Card.Header>

        <Card.Body >
          {loading ? (
            <div className="text-center py-5 d-flex align-items-center justify-content-center">
              <Spinner animation="border" role="status" className="me-2">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <span>{t.loadingTasks}</span>
            </div>
          ) : editingTask ? (
            <ChapterTextEditor entity={editingTask} onBack={handleTaskContentBack} />
          ) : previewTask ? (
            <ChapterTextEditor entity={previewTask} oldEntity={oldContent} onBack={handlePreviewBack} readOnly={true} />
          ) : (
            <>
              {activeTab === 'assigned' && (
                <div>
                  {tasks.length > 0 ? (
                    <ListGroup variant="flush">
                      {tasks.map(task => (
                        <ListGroup.Item key={task.id} className="border rounded py-3">
                          <TaskDetails task={task} translations={t} handleDownload={handleDownload} />
                          {activeStatusTab === 'OPEN' && (
                            <div className="d-flex gap-2 mt-3">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleFetchChapterContent(task, 'edit')}
                              >
                                {t.editContent}
                              </Button>
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleUpdateTaskState(task.id, 'WAITING')}
                              >
                                {t.markAsFinished}
                              </Button>
                            </div>
                          )}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <div className="text-center text-muted py-4">
                      {t.noTasksFound}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'owned' && (
                <div>
                  {ownedChapterTasks.length > 0 ? (
                    <ListGroup variant="flush">
                      {ownedChapterTasks.map(task => (
                        <ListGroup.Item key={task.id} className="border rounded py-3 mb-2">
                          <OwnedTaskDetails task={task} t={t} handleDownload={handleDownload} />

                          <div className="mt-3">
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleFetchChapterContent(task, 'preview')}
                              className="me-2"
                            >
                              {t.preview || 'Visualizar'}
                            </Button>
                            {activeStatusTab === 'OPEN' && (
                              <>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => { setShowChangeResponsibleModal(true); setSelectedTaskForChange(task) }}
                                >
                                  {t.changeResponsible}
                                </Button>
                              </>
                            )}
                            {activeStatusTab === 'WAITING' && (
                              <>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleUpdateTaskStateWithListUpdate(task.id, 'OPEN')}
                                  className="me-2"
                                >
                                  {t.reopen || 'Reabrir'}
                                </Button>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => handleUpdateTaskStateWithListUpdate(task.id, 'CLOSED')}
                                >
                                  {t.close || 'Fechar'}
                                </Button>
                              </>
                            )}
                            {activeStatusTab === 'CLOSED' && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleUpdateTaskStateWithListUpdate(task.id, 'OPEN')}
                              >
                                {t.reopen || 'Reabrir'}
                              </Button>
                            )}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <div className="text-center text-muted py-4">
                      {t.noTasksFound}
                    </div>
                  )}
                </div>
              )}

              <PaginationComponent
                totalPages={totalPages}
                currentPage={currentPage}
                setCurrentPage={handlePageChange}
                lengthTotal={totalTasks}
                tItemName={t.commentsLow}
                t={t}
                indexOfFirst={indexOfFirstTask}
                indexOfLast={indexOfLastTask}
              />
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showCreateTaskModal} onHide={handleCloseCreateTaskModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t.createNewTask}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>{t.selectChapter}</strong></Form.Label>
              <BrandSelect
                className="w-100"
                value={selectedChapterIdForNewTask}
                onChange={(v) => handleChapterSelectForNewTask(v)}
                placeholder={`-- ${t.selectChapter} --`}
                options={[
                  { value: "", label: `-- ${t.selectChapter} --` },
                  ...availableChapters.map(chapter => ({ value: chapter.id, label: `${chapter.title} (${chapter.book.title})` })),
                ]}
              />
            </Form.Group>

            {selectedChapterIdForNewTask && (
              <Form.Group className="mb-3">
                <Form.Label><strong>{t.selectComments}</strong></Form.Label>
                {commentsForSelectedChapter.length > 0 ? (
                  <ListGroup style={{ maxHeight: '200px', overflowY: 'auto' }} className="border rounded p-2">
                    {commentsForSelectedChapter.map(comment => (
                      <ListGroup.Item key={comment.id} className="d-flex align-items-center ps-0 border-0">
                        <Form.Check
                          type="checkbox"
                          id={`comment-${comment.id}`}
                          checked={selectedCommentsForNewTask.includes(comment.id)}
                          onChange={() => toggleCommentSelectionForNewTask(comment.id)}
                          className="me-2"
                        />
                        <label htmlFor={`comment-${comment.id}`} className="flex-grow-1">
                          "{comment.text}"
                          {comment.pageNumber && <Badge bg="secondary" className="ms-1">{t.pageNumber}{comment.pageNumber}</Badge>}
                          {comment.sugestionType && <Badge bg="info" className="ms-1">{comment.sugestionType}</Badge>}
                          {comment.imageBase64 && comment.nameImageBase64 && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="ms-2"
                              onClick={() => handleDownload(comment.imageBase64, comment.nameImageBase64)}
                            >
                              Download: {comment.nameBase64}
                            </Button>
                          )}
                          <span className="text-muted small d-block"> - {comment.creator.name} ({new Date(comment.createdAt).toLocaleDateString()})</span>
                        </label>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div className="text-muted">{t.noCommentsAvailable}</div>
                )}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label><strong>{t.selectEditor}</strong></Form.Label>
              <BrandSelect
                className="w-100"
                value={selectedEditorForNewTask}
                onChange={(v) => setSelectedEditorForNewTask(v)}
                placeholder={`-- ${t.selectEditor} --`}
                options={[
                  { value: "", label: `-- ${t.selectEditor} --` },
                  ...availableEditors.map(editor => ({ value: editor.id, label: editor.name })),
                ]}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>{t.taskInstructions}</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder={t.instructions}
                value={newTaskInstructions}
                onChange={(e) => setNewTaskInstructions(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateTaskModal}>
            {t.cancel}
          </Button>
          <Button variant="primary" onClick={handleCreateNewTask}>
            {t.createTask}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showChangeResponsibleModal} onHide={() => setShowChangeResponsibleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t.changeResponsible || 'Alterar Responsável'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>

          <Form.Group className="mb-3">
            <Form.Label className="me-2">
              <strong>{t.chooseResponsible}</strong>
            </Form.Label>
            <BrandSelect
              value={selectedTaskForChange?.responsible.id}
              onChange={(v) => handleUpdateTaskResponsible(selectedTaskForChange.id, v, (availableEditors.find(ed => String(ed.id) === String(v)) || {}).name)}
              style={{ maxWidth: '300px' }}
              options={availableEditors.map(editor => ({ value: editor.id, label: editor.name }))}
            />
          </Form.Group>
          <p>{t.taskDetails}:</p>
          <Card className="p-3 mb-3">
            <OwnedTaskDetails task={selectedTaskForChange} t={t} handleDownload={handleDownload} />
          </Card>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default TaskManagement;
